// @flow
import React, { Fragment } from 'react';
import Dropdown from '@localytics/loc-components/lib/Dropdown';
import { pullAt, clone, isEqual, set as _set, get as _get } from 'lodash';
import cs from 'classnames';
import '@localytics/push-message-builder/src/components/stylesheets/FormField.scss';
import { isNumber } from '../../util/NumberUtils';
import {
  stringOperators,
  numberOperators,
  generalOperators,
  typeFromOperator
} from '../../util/AttributeUtils';

import type { Operator, OperatorOptionsType } from '../../constants/ApiTypes';
import type {
  EventMapping,
  AttributeGroup
} from '../../constants/RailsApiTypes';

type EventAttributesFormState = {
  selectedAttribute: ?AttributeGroup,
  selectedOperator: ?(Operator | OperatorOptionsType),
  selectedValues: ?Array<string>
};

type EventAttributesFormProps = {
  onAttributeUpdate: Function,
  onOperatorUpdate: Function,
  onAttributeValuesUpdate: Function,

  selectedEvent: EventMapping,
  selectedAttribute: ?AttributeGroup,
  selectedOperator: ?(Operator | OperatorOptionsType),
  selectedValues: Array<string>,

  disabled?: boolean
};

const allOperators = stringOperators
  .concat(numberOperators)
  .concat(generalOperators);

export class EventAttributesForm extends React.Component<
  EventAttributesFormProps,
  EventAttributesFormState
> {
  constructor(props: EventAttributesFormProps) {
    super(props);
    const { selectedAttribute, selectedOperator, selectedValues } = props;
    const operator =
      allOperators.find(o => o.id === selectedOperator) || stringOperators[0];
    const selectedAttributeWithLabel = _set(
      clone(selectedAttribute),
      'label',
      _get(selectedAttribute, 'name')
    );
    this.state = {
      selectedAttribute: selectedAttributeWithLabel,
      selectedOperator: operator,
      selectedValues
    };
  }

  componentWillReceiveProps(nextProps: EventAttributesFormProps) {
    const {
      selectedAttribute: nextAttribute,
      selectedOperator: nextOperator
    } = nextProps;
    const { selectedAttribute, selectedOperator } = this.props;
    if (!isEqual(selectedAttribute, nextAttribute)) {
      this.setState({
        selectedAttribute: nextAttribute,
        selectedOperator: stringOperators[0],
        selectedValues: []
      });
    }

    if (!isEqual(selectedOperator, nextOperator)) {
      this.setState({
        selectedOperator: nextOperator || stringOperators[0],
        selectedValues: []
      });
    }
  }

  setValues = (values: Array<string>) => {
    const { onAttributeValuesUpdate } = this.props;
    const clean = values.filter(i => !!i);
    this.setState(
      { selectedValues: clean },
      () => onAttributeValuesUpdate && onAttributeValuesUpdate(clean)
    );
  };
  updateAttribute = (e: AttributeGroup) => {
    const change: {
      selectedAttribute: AttributeGroup,
      selectedValues?: Array<string>
    } = { selectedAttribute: e };
    const { onAttributeUpdate } = this.props;
    if (!e) {
      change.selectedValues = undefined;
    }
    this.setState(change, () => onAttributeUpdate && onAttributeUpdate(e));
  };
  updateOperator = (e: OperatorOptionsType) => {
    const { onOperatorUpdate } = this.props;
    this.setState(
      { selectedOperator: e },
      () => onOperatorUpdate && onOperatorUpdate(e)
    );
  };
  updateValue = (val: string, index: number) => {
    const { selectedValues, selectedOperator } = this.state;
    const values = clone(selectedValues || []);
    const coercedVal =
      typeFromOperator(_get(selectedOperator, 'id', 'EQ')) === 'NUMBER' &&
      isNumber(val)
        ? Number.parseFloat(val)
        : val;
    values[index] = coercedVal;
    this.setValues(values);
  };
  removeValue = (index: number) => {
    const { selectedValues } = this.state;
    const values = clone(selectedValues);
    pullAt(values, index);
    this.setValues(values);
  };
  render() {
    const { selectedEvent, disabled } = this.props;
    const { selectedAttribute, selectedOperator, selectedValues } = this.state;
    const attributesOptions = _get(selectedEvent, 'attribute_groups', []).map(
      ag => ({
        label: ag.name,
        value: ag.id,
        ...ag
      })
    );
    const eventName = _get(selectedEvent, 'name');

    const operatorOptions = allOperators;

    const toConcat = selectedAttribute ? [''] : [];
    const preValue = selectedValues || toConcat;
    const values =
      _get(selectedOperator, 'multiple', false) || preValue.length === 0
        ? preValue.concat(toConcat)
        : preValue;
    return (
      <Fragment>
        <div className="EventNodeModal__FormRow  EventNodeModal__FormRow--Field">
          <div className="EventNodeModal__FormLeft">
            <span
              style={{
                color: '#3F404A',
                fontWeight: '600',
                display: 'inline-block'
              }}
            >
              Where
            </span>{' '}
          </div>

          <div className="EventNodeModal__FormRight">
            <div className="EventNodeModal__InputsContainer">
              <Dropdown
                name="attribute"
                className="EventNodeModal__DropdownWide"
                selectedValue={selectedAttribute}
                options={attributesOptions}
                onSelect={this.updateAttribute}
                placeholder={`Choose an attribute of '${eventName}'`}
                disabled={disabled}
              />
              {selectedAttribute && (
                <Dropdown
                  name="operator"
                  className="EventNodeModal__DropdownWide"
                  selectedValue={selectedOperator}
                  options={operatorOptions}
                  onSelect={this.updateOperator}
                  clearable={false}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </div>
        {values.map((value, i) => (
          <div
            key={
              `${i}` // eslint-disable-line react/no-array-index-key
            }
            className="EventNodeModal__FormRow"
          >
            <div className="EventNodeModal__FormLeft" />
            <div className="EventNodeModal__FormRight">
              <div className="EventNodeModal__InputsContainer">
                {i > 0 && (
                  <span className="EventNodeModal__InputPrepend">or</span>
                )}
                <input
                  onChange={e => this.updateValue(e.target.value, i)}
                  value={value}
                  disabled={disabled}
                  placeholder={
                    i > 0
                      ? `Enter another value for '${_get(
                          selectedAttribute,
                          'name'
                        )}'`
                      : `Enter a value for '${_get(selectedAttribute, 'name')}'`
                  }
                  name="value"
                  className={cs('EventNodeModal__Input', {
                    'EventNodeModal__Input--multi': i > 0
                  })}
                />
                {i > 0 && (
                  <button
                    className="EventNodeModal__RemoveButton"
                    onClick={() => this.removeValue(i)}
                    disabled={disabled}
                  >
                    x
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </Fragment>
    );
  }
}

export default EventAttributesForm;



// WEBPACK FOOTER //
// ./src/components/EventAttributes/EventAttributesForm.jsx